import Add20 from '@carbon/icons-react/es/add/20';
import { useBoolean } from 'ahooks';
import { Tooltip } from 'antd';
import moment from 'moment';
import React, { useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Droppable, Draggable } from 'react-virtualized-dnd';
import { ProjectId } from 'api/models';
import { Button } from 'components/atoms/button';
import { Board } from 'components/molecules/board';
import {
  useColumnContext,
  ColumnContext,
  ColumnContextValue,
} from 'components/organisms/kanban-board/coulmn-context';
import { Item } from 'components/organisms/kanban-board/item';
import { NewTaskCardWithProject } from 'components/organisms/kanban-board/new-task-card-with-project';
import {
  generateDraggableId,
  generateDroppableId,
} from 'components/organisms/kanban-board/utils';
import { PermissionRuleName } from 'constant';
import { CardDimension, COLUMN_HEADER_HEIGHT, ItemDimension } from './common';
import {
  BoardColumnHeaderTitle,
  BoardColumnHeaderCount,
  BoardColumnHeaderRightAction,
} from './styled';
import type { Column as ColumnT } from './types';
import { OnCreateFn } from './types';

interface ColumnProps {
  items: any[];
  column: ColumnT;
  rowId: string;
  row: ColumnT | undefined | null;
  onCreate: null | OnCreateFn;
  height: number;
  currentProjectId: ProjectId | null;
}

type Data = { itemId: string; rowId: string; columnId: string };
type NewItem = ['new', Data];
type LoadingItem = ['loading', Data];
type LoadedItem = ['loaded', Data];

type ItemT = NewItem | LoadingItem | LoadedItem;
type Items = ItemT[];

interface SortableItemProps {
  id: ItemT;
  currentProjectId: ProjectId | null;
}
function SortableItem({
  id: [status, id],
  currentProjectId,
}: SortableItemProps) {
  const { rowId, columnId, itemId } = id;
  const { onCreate, onCancel, inputRef } = useColumnContext();
  const draggableId = generateDraggableId(rowId, columnId, itemId);

  return (
    <Board.Body.Column.Container.List.Container.ItemContainer.Item
      style={{
        paddingBottom: '8px',
        height: status === 'new' ? 'auto' : ItemDimension.height,
        width: ItemDimension.width,
      }}
    >
      {status === 'new' ? (
        <NewTaskCardWithProject
          currentProjectId={currentProjectId}
          inputRef={inputRef}
          onCreate={onCreate}
          onCancel={onCancel}
        />
      ) : (
        <Item itemId={draggableId} />
      )}
    </Board.Body.Column.Container.List.Container.ItemContainer.Item>
  );
}

export const Column = React.memo(function Column({
  column,
  items: givenItems,
  rowId,
  onCreate,
  row,
  height,
  currentProjectId,
}: ColumnProps) {
  const { colId, label, color } = column;

  const [
    canShowNewTaskCard,
    { setFalse: closeNewTaskCard, setTrue: showNewTaskCard },
  ] = useBoolean(false);

  const [creatingTasks] = useState([]);

  const items = useMemo((): Items => {
    let resultItems: Items = [];
    const loaded: LoadedItem[] = givenItems.map((item) => {
      return [
        'loaded',
        {
          rowId,
          columnId: colId,
          itemId: item,
        },
      ];
    });

    resultItems = [...creatingTasks, ...loaded];

    if (canShowNewTaskCard) {
      resultItems = [
        ['new', { itemId: String(moment().valueOf()), columnId: colId, rowId }],
        ...loaded,
      ];
    }

    return resultItems;
  }, [rowId, colId, givenItems, canShowNewTaskCard, creatingTasks]);

  const droppableId = generateDroppableId(rowId, colId);
  const inputRef = useRef<HTMLInputElement>(null);
  const contextValue = useMemo((): ColumnContextValue => {
    const handleOnCreate = (payload) => {
      if (onCreate) {
        onCreate(payload, column, row);
      }

      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.value = '';
      }
    };

    return {
      onCreate: handleOnCreate,
      onCancel: () => {
        closeNewTaskCard();
      },
      inputRef,
    };
  }, [column, onCreate, closeNewTaskCard, inputRef, row]);

  return (
    <Board.Body.ColumnWrapper key={droppableId + 1}>
      <Board.Body.Column data-cy={label}>
        <ColumnContext.Provider value={contextValue}>
          <Droppable
            activeHeaderClass="header-active"
            virtualizationThreshold={20}
            enforceContainerMinHeight
            listHeader={
              <Board.Body.Column.Header>
                <BoardColumnHeaderCount>
                  {canShowNewTaskCard ? items.length - 1 : items.length}
                </BoardColumnHeaderCount>
                <BoardColumnHeaderTitle color={color} title={label}>
                  {label}
                </BoardColumnHeaderTitle>
                {onCreate ? (
                  <BoardColumnHeaderRightAction>
                    <Tooltip title={<FormattedMessage id="task.add" />}>
                      <Button
                        disabled={column.disableAdd}
                        type="text"
                        icon={<Add20 />}
                        onClick={showNewTaskCard}
                        data-cy="project.board.statusTile.newTask"
                        ruleName={PermissionRuleName.TaskCreateAction}
                      />
                    </Tooltip>
                  </BoardColumnHeaderRightAction>
                ) : null}
              </Board.Body.Column.Header>
            }
            listHeaderHeight={COLUMN_HEADER_HEIGHT}
            elemHeight={ItemDimension.height}
            containerHeight={height}
            dragAndDropGroup="name1"
            droppableId={droppableId}
            key={droppableId}
            placeholderStyle={{
              border: '2px dashed var(--blue-60)',
              backgroundColor: 'var(--blue-10)',
              marginBottom: '8px',
              width: CardDimension.widthPx,
              height: CardDimension.heightPx,
              minHeight: CardDimension.heightPx,
            }}
          >
            {items.map((value) => {
              const [status] = value;
              const draggableId = generateDraggableId(
                rowId,
                colId,
                value[1].itemId,
              );

              return (
                <Draggable
                  dragAndDropGroup="name1"
                  draggableId={draggableId}
                  droppableId={droppableId}
                  dragDisabled={false}
                  key={draggableId}
                  disabled={status === 'new' || status === 'loading'}
                >
                  <SortableItem
                    id={value}
                    currentProjectId={currentProjectId}
                  />
                </Draggable>
              );
            })}
          </Droppable>
        </ColumnContext.Provider>
      </Board.Body.Column>
    </Board.Body.ColumnWrapper>
  );
});
